import { AuthShell, ResetPasswordForm } from "@client/components/auth";
import { Button } from "@client/components/ui/button";
import { createRouteErrorComponent } from "@client/lib/sentry";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertCircle } from "lucide-react";
import { z } from "zod";

const resetPasswordSearchSchema = z.object({
	token: z.string().optional(),
	error: z.string().optional(),
});

export const Route = createFileRoute("/(auth)/reset-password")({
	component: ResetPasswordPage,
	validateSearch: resetPasswordSearchSchema,
	errorComponent: createRouteErrorComponent({
		routePath: "/reset-password",
		routeName: "Reset Password",
		customMessage: "Error loading the password reset page",
	}),
});

function ResetPasswordPage() {
	const { token, error } = Route.useSearch();

	// Better Auth redirects here with ?error=INVALID_TOKEN when the link is
	// expired or already used, and without a token if it was tampered with.
	if (error === "INVALID_TOKEN" || !token) {
		const message =
			error === "INVALID_TOKEN"
				? "This password reset link is invalid or has expired."
				: "No reset token was found. Open the link from your email, or request a new one.";

		return (
			<AuthShell
				title="Link no longer valid"
				description="Reset links expire one hour after they're sent."
			>
				<div className="space-y-4">
					<div className="flex items-start gap-2 rounded-lg border border-destructive/20 bg-destructive/10 p-3">
						<AlertCircle className="mt-0.5 h-4 w-4 text-destructive" />
						<span className="text-sm">{message}</span>
					</div>
					<Button asChild className="w-full">
						<Link to="/forgot-password">Request a new link</Link>
					</Button>
					<p className="text-center text-muted-foreground text-sm">
						<Link to="/" className="text-primary hover:underline">
							← Back to sign in
						</Link>
					</p>
				</div>
			</AuthShell>
		);
	}

	return (
		<AuthShell
			title="Choose a new password"
			description="Pick something you haven't used before."
		>
			<ResetPasswordForm token={token} />
		</AuthShell>
	);
}
